import './sentry'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import './assets/css/main.css'
import App from './App'
import { CURRENT_BUILD, startAppVersionPolling } from './features/pwa/appVersion'
import { initYm } from './ym'

// Early `beforeinstallprompt` capture (Android Chrome). Chrome выстреливает event
// один раз как только PWA install criteria met — это часто раньше чем React
// mount'ится и `useInstallPrompt` успевает добавить listener в useEffect.
// Без early capture event теряется → canInstall=false → InstallBanner не показывается.
// useInstallPrompt при mount читает window.__pg19_deferredInstallPrompt__ и
// подхватывает уже-пойманный event.
declare global {
  interface Window {
    __pg19_deferredInstallPrompt__?: Event
  }
}
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  window.__pg19_deferredInstallPrompt__ = e
  // Сигнал React'у пере-проверить state (если он уже mount'ился)
  window.dispatchEvent(new CustomEvent('pg19:bip-ready'))
})

// Service worker auto-registration. registerType: 'autoUpdate' (vite.config.ts) →
// SW silently обновляется. virtual:pwa-register сам слушает workbox `activated`
// event и делает window.location.reload() ТОЛЬКО при event.isUpdate || isExternal
// (см. vite-plugin-pwa/src/client/build/register.ts). На first install reload НЕ
// делается — это by design. Custom controllerchange listener здесь раньше ломал
// именно first install (триггерил лишний reload), поэтому удалён.
//
// iOS Safari throttles SW update checks (default ~24h). Без явного poll'инга юзер
// застревает на старом bundle после deploy ДНИ. Решение: каждые 60 сек дёргаем
// registration.update() пока страница открыта + immediate check на visibilitychange
// (focus tab/foreground PWA).
registerSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return

    console.info(
      `[app version] ${CURRENT_BUILD.version} ${CURRENT_BUILD.gitSha} built ${CURRENT_BUILD.buildTime}`,
    )
    startAppVersionPolling(registration)

    // Periodic update check while page is open
    setInterval(() => {
      registration.update().catch(() => {})
    }, 60 * 1000)

    // Immediate check when tab/PWA becomes visible (foreground)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        registration.update().catch(() => {})
      }
    })
  },
})

initYm()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
